import { matchPath } from 'react-router-dom'

export interface AppCrumb {
  label: string
  to?: string
}

function titleCase(slug: string): string {
  return slug
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

type CrumbSpec = {
  path: string
  end?: boolean
  crumbs: (params: Record<string, string | undefined>) => AppCrumb[]
}

/** Most specific paths first (matchPath is evaluated in order). */
const DASHBOARD_SPECS: CrumbSpec[] = [
  { path: '/dashboard', end: true, crumbs: () => [{ label: 'Dashboard' }] },
  { path: '/dashboard/admin', end: true, crumbs: () => [{ label: 'Dashboard', to: '/dashboard' }, { label: 'Platform admin' }] },
  { path: '/dashboard/admin/quota', end: true, crumbs: () => [{ label: 'Dashboard', to: '/dashboard' }, { label: 'Platform admin', to: '/dashboard/admin' }, { label: 'Quota' }] },
  { path: '/dashboard/admin/security', end: true, crumbs: () => [{ label: 'Dashboard', to: '/dashboard' }, { label: 'Platform admin', to: '/dashboard/admin' }, { label: 'Shop security' }] },
  { path: '/dashboard/admin/payment-gateways', end: true, crumbs: () => [{ label: 'Dashboard', to: '/dashboard' }, { label: 'Platform admin', to: '/dashboard/admin' }, { label: 'Payment gateways' }] },
  { path: '/dashboard/admin/host-backups', end: true, crumbs: () => [{ label: 'Dashboard', to: '/dashboard' }, { label: 'Host backups' }] },
  { path: '/dashboard/customers/new', end: true, crumbs: () => [{ label: 'Customers', to: '/dashboard/customers' }, { label: 'New customer' }] },
  {
    path: '/dashboard/customers/:customerId/edit',
    end: true,
    crumbs: (p) => [{ label: 'Customers', to: '/dashboard/customers' }, { label: `Edit customer #${p.customerId}` }],
  },
  {
    path: '/dashboard/customers/:customerId',
    end: true,
    crumbs: (p) => [{ label: 'Customers', to: '/dashboard/customers' }, { label: `Customer #${p.customerId}` }],
  },
  { path: '/dashboard/customers', end: true, crumbs: () => [{ label: 'Customers' }] },
  { path: '/dashboard/products/new', end: true, crumbs: () => [{ label: 'Products', to: '/dashboard/products' }, { label: 'New product' }] },
  {
    path: '/dashboard/products/:productId/stats',
    end: true,
    crumbs: (p) => [{ label: 'Products', to: '/dashboard/products' }, { label: `Product #${p.productId}`, to: `/dashboard/products/${p.productId}` }, { label: 'Statistics' }],
  },
  {
    path: '/dashboard/products/:productId/edit',
    end: true,
    crumbs: (p) => [{ label: 'Products', to: '/dashboard/products' }, { label: `Edit product #${p.productId}` }],
  },
  {
    path: '/dashboard/products/:productId',
    end: true,
    crumbs: (p) => [{ label: 'Products', to: '/dashboard/products' }, { label: `Product #${p.productId}` }],
  },
  { path: '/dashboard/products', end: true, crumbs: () => [{ label: 'Products' }] },
  { path: '/dashboard/orders/new', end: true, crumbs: () => [{ label: 'Sales Orders', to: '/dashboard/orders' }, { label: 'New order' }] },
  { path: '/dashboard/orders/stats', end: true, crumbs: () => [{ label: 'Sales Orders', to: '/dashboard/orders' }, { label: 'Statistics' }] },
  {
    path: '/dashboard/orders/:orderId/edit',
    end: true,
    crumbs: (p) => [{ label: 'Sales Orders', to: '/dashboard/orders' }, { label: `Edit order #${p.orderId}` }],
  },
  {
    path: '/dashboard/orders/:orderId',
    end: true,
    crumbs: (p) => [{ label: 'Sales Orders', to: '/dashboard/orders' }, { label: `Order #${p.orderId}` }],
  },
  { path: '/dashboard/orders', end: true, crumbs: () => [{ label: 'Sales Orders' }] },
  { path: '/dashboard/quotations/new', end: true, crumbs: () => [{ label: 'Quotations', to: '/dashboard/quotations' }, { label: 'New quotation' }] },
  {
    path: '/dashboard/quotations/:quotationId/edit',
    end: true,
    crumbs: (p) => [{ label: 'Quotations', to: '/dashboard/quotations' }, { label: `Edit quotation #${p.quotationId}` }],
  },
  {
    path: '/dashboard/quotations/:quotationId',
    end: true,
    crumbs: (p) => [{ label: 'Quotations', to: '/dashboard/quotations' }, { label: `Quotation #${p.quotationId}` }],
  },
  { path: '/dashboard/quotations', end: true, crumbs: () => [{ label: 'Quotations' }] },
  { path: '/dashboard/payments/new', end: true, crumbs: () => [{ label: 'Payments', to: '/dashboard/payments' }, { label: 'New payment' }] },
  { path: '/dashboard/payments/stats', end: true, crumbs: () => [{ label: 'Payments', to: '/dashboard/payments' }, { label: 'Statistics' }] },
  {
    path: '/dashboard/payments/:paymentId/edit',
    end: true,
    crumbs: (p) => [{ label: 'Payments', to: '/dashboard/payments' }, { label: `Edit payment #${p.paymentId}` }],
  },
  {
    path: '/dashboard/payments/:paymentId',
    end: true,
    crumbs: (p) => [{ label: 'Payments', to: '/dashboard/payments' }, { label: `Payment #${p.paymentId}` }],
  },
  { path: '/dashboard/payments', end: true, crumbs: () => [{ label: 'Payments' }] },
  {
    path: '/dashboard/website/pages/:pageId',
    end: true,
    crumbs: (p) => [
      { label: 'Web Designer', to: '/dashboard/website' },
      { label: `Page #${p.pageId}` },
    ],
  },
  {
    path: '/dashboard/website/blog',
    end: true,
    crumbs: () => [{ label: 'Website', to: '/dashboard/website' }, { label: 'Blog / Announcements' }],
  },
  {
    path: '/dashboard/website/templates',
    end: true,
    crumbs: () => [{ label: 'Website', to: '/dashboard/website' }, { label: 'Page templates' }],
  },
  {
    path: '/dashboard/website/analytics',
    end: true,
    crumbs: () => [{ label: 'Website', to: '/dashboard/website' }, { label: 'Analytics' }],
  },
  {
    path: '/dashboard/website/reviews',
    end: true,
    crumbs: () => [{ label: 'Website', to: '/dashboard/website' }, { label: 'Product reviews' }],
  },
  { path: '/dashboard/events/new', end: true, crumbs: () => [{ label: 'Events & Tickets', to: '/dashboard/events' }, { label: 'New event' }] },
  {
    path: '/dashboard/events/:eventId/tickets',
    end: true,
    crumbs: (p) => [
      { label: 'Events & Tickets', to: '/dashboard/events' },
      { label: `Event #${p.eventId} · tickets` },
    ],
  },
  {
    path: '/dashboard/events/:eventId/edit',
    end: true,
    crumbs: (p) => [
      { label: 'Events & Tickets', to: '/dashboard/events' },
      { label: `Edit event #${p.eventId}` },
    ],
  },
  { path: '/dashboard/events/forms/new', end: true, crumbs: () => [{ label: 'Events & Tickets', to: '/dashboard/events' }, { label: 'Forms', to: '/dashboard/events/forms' }, { label: 'New form' }] },
  {
    path: '/dashboard/events/forms/:formId/submissions',
    end: true,
    crumbs: (p) => [
      { label: 'Events & Tickets', to: '/dashboard/events' },
      { label: 'Forms', to: '/dashboard/events/forms' },
      { label: `Form #${p.formId} · submissions` },
    ],
  },
  {
    path: '/dashboard/events/forms/:formId/edit',
    end: true,
    crumbs: (p) => [
      { label: 'Events & Tickets', to: '/dashboard/events' },
      { label: 'Forms', to: '/dashboard/events/forms' },
      { label: `Edit form #${p.formId}` },
    ],
  },
  { path: '/dashboard/events/forms', end: true, crumbs: () => [{ label: 'Events & Tickets', to: '/dashboard/events' }, { label: 'Forms' }] },
  { path: '/dashboard/messaging/templates', end: true, crumbs: () => [{ label: 'Messaging', to: '/dashboard/messaging/send' }, { label: 'Templates' }] },
  { path: '/dashboard/messaging/send', end: true, crumbs: () => [{ label: 'Send message' }] },
  { path: '/dashboard/support', end: true, crumbs: () => [{ label: 'Support Chat' }] },
  { path: '/dashboard/shops/new', end: true, crumbs: () => [{ label: 'Shops', to: '/dashboard/shops' }, { label: 'New shop' }] },
  {
    path: '/dashboard/shops/:shopId/edit',
    end: true,
    crumbs: (p) => [{ label: 'Shops', to: '/dashboard/shops' }, { label: `Edit shop #${p.shopId}` }],
  },
  {
    path: '/dashboard/shops/:shopId',
    end: true,
    crumbs: (p) => [{ label: 'Shops', to: '/dashboard/shops' }, { label: `Shop #${p.shopId}` }],
  },
  { path: '/dashboard/shops', end: true, crumbs: () => [{ label: 'Shops' }] },
  { path: '/dashboard/branches', end: true, crumbs: () => [{ label: 'Branches' }] },
  { path: '/dashboard/staff/new', end: true, crumbs: () => [{ label: 'Staff', to: '/dashboard/staff' }, { label: 'New staff' }] },
  {
    path: '/dashboard/staff/:staffId/edit',
    end: true,
    crumbs: (p) => [{ label: 'Staff', to: '/dashboard/staff' }, { label: `Edit #${p.staffId}` }],
  },
  {
    path: '/dashboard/staff/:staffId',
    end: true,
    crumbs: (p) => [{ label: 'Staff', to: '/dashboard/staff' }, { label: `Staff #${p.staffId}` }],
  },
  { path: '/dashboard/staff', end: true, crumbs: () => [{ label: 'Staff' }] },
  {
    path: '/dashboard/brands/:brandId/edit',
    end: true,
    crumbs: (p) => [{ label: 'Brands', to: '/dashboard/brands' }, { label: `Edit #${p.brandId}` }],
  },
  { path: '/dashboard/brands', end: true, crumbs: () => [{ label: 'Brands' }] },
  { path: '/dashboard/suppliers/new', end: true, crumbs: () => [{ label: 'Suppliers', to: '/dashboard/suppliers' }, { label: 'New supplier' }] },
  {
    path: '/dashboard/suppliers/:supplierId/addresses/new',
    end: true,
    crumbs: (p) => [
      { label: 'Suppliers', to: '/dashboard/suppliers' },
      { label: `Supplier #${p.supplierId}`, to: `/dashboard/suppliers/${p.supplierId}` },
      { label: 'New address' },
    ],
  },
  {
    path: '/dashboard/suppliers/:supplierId/addresses/:addressId/edit',
    end: true,
    crumbs: (p) => [
      { label: 'Suppliers', to: '/dashboard/suppliers' },
      { label: `Supplier #${p.supplierId}`, to: `/dashboard/suppliers/${p.supplierId}` },
      { label: `Edit address #${p.addressId}` },
    ],
  },
  {
    path: '/dashboard/suppliers/:supplierId/edit',
    end: true,
    crumbs: (p) => [{ label: 'Suppliers', to: '/dashboard/suppliers' }, { label: `Edit #${p.supplierId}` }],
  },
  {
    path: '/dashboard/suppliers/:supplierId',
    end: true,
    crumbs: (p) => [{ label: 'Suppliers', to: '/dashboard/suppliers' }, { label: `Supplier #${p.supplierId}` }],
  },
  { path: '/dashboard/suppliers', end: true, crumbs: () => [{ label: 'Suppliers' }] },
  { path: '/dashboard/purchase-orders/new', end: true, crumbs: () => [{ label: 'Purchase Orders', to: '/dashboard/purchase-orders' }, { label: 'New purchase order' }] },
  {
    path: '/dashboard/purchase-orders/:purchaseOrderId/edit',
    end: true,
    crumbs: (p) => [
      { label: 'Purchase Orders', to: '/dashboard/purchase-orders' },
      { label: `Edit PO #${p.purchaseOrderId}` },
    ],
  },
  {
    path: '/dashboard/purchase-orders/:purchaseOrderId',
    end: true,
    crumbs: (p) => [
      { label: 'Purchase Orders', to: '/dashboard/purchase-orders' },
      { label: `PO #${p.purchaseOrderId}` },
    ],
  },
  { path: '/dashboard/purchase-orders', end: true, crumbs: () => [{ label: 'Purchase Orders' }] },
  {
    path: '/dashboard/subscription/:subscriptionId',
    end: true,
    crumbs: (p) => [
      { label: 'Subscription', to: '/dashboard/subscription' },
      { label: p.subscriptionId ? `#${p.subscriptionId}` : 'Detail' },
    ],
  },
  { path: '/dashboard/subscription', end: true, crumbs: () => [{ label: 'Subscription' }] },
  {
    path: '/dashboard/leases/:subscriptionId',
    end: true,
    crumbs: (p) => [
      { label: 'Rentals / Leases', to: '/dashboard/leases' },
      { label: p.subscriptionId ? `#${p.subscriptionId}` : 'Detail' },
    ],
  },
  { path: '/dashboard/leases', end: true, crumbs: () => [{ label: 'Rentals / Leases' }] },
  { path: '/dashboard/tags/new', end: true, crumbs: () => [{ label: 'Tags', to: '/dashboard/tags' }, { label: 'New tag' }] },
  {
    path: '/dashboard/tags/:tagId/edit',
    end: true,
    crumbs: (p) => [{ label: 'Tags', to: '/dashboard/tags' }, { label: `Edit tag #${p.tagId}` }],
  },
  {
    path: '/dashboard/tags/:tagId',
    end: true,
    crumbs: (p) => [{ label: 'Tags', to: '/dashboard/tags' }, { label: `Tag #${p.tagId}` }],
  },
  { path: '/dashboard/tags', end: true, crumbs: () => [{ label: 'Tags' }] },
  {
    path: '/dashboard/referrers/new',
    end: true,
    crumbs: () => [{ label: 'Referrers', to: '/dashboard/referrers' }, { label: 'New referrer' }],
  },
  {
    path: '/dashboard/referrers/:referrerId/edit',
    end: true,
    crumbs: (p) => [
      { label: 'Referrers', to: '/dashboard/referrers' },
      { label: `Edit referrer #${p.referrerId}` },
    ],
  },
  {
    path: '/dashboard/referrers/:referrerId',
    end: true,
    crumbs: (p) => [
      { label: 'Referrers', to: '/dashboard/referrers' },
      { label: `Referrer #${p.referrerId}` },
    ],
  },
  { path: '/dashboard/referrers', end: true, crumbs: () => [{ label: 'Referrers' }] },
  { path: '/dashboard/website', end: true, crumbs: () => [{ label: 'Web Designer' }] },
  { path: '/dashboard/coupons/templates/new', end: true, crumbs: () => [{ label: 'Coupons', to: '/dashboard/coupons' }, { label: 'New template' }] },
  {
    path: '/dashboard/coupons/templates/:templateId/edit',
    end: true,
    crumbs: (p) => [{ label: 'Coupons', to: '/dashboard/coupons' }, { label: `Edit template #${p.templateId}` }],
  },
  {
    path: '/dashboard/coupons/templates/:templateId',
    end: true,
    crumbs: (p) => [{ label: 'Coupons', to: '/dashboard/coupons' }, { label: `Template #${p.templateId}` }],
  },
  { path: '/dashboard/coupons/rules/new', end: true, crumbs: () => [{ label: 'Coupons', to: '/dashboard/coupons' }, { label: 'New grant rule' }] },
  {
    path: '/dashboard/coupons/rules/:ruleId/edit',
    end: true,
    crumbs: (p) => [{ label: 'Coupons', to: '/dashboard/coupons' }, { label: `Edit rule #${p.ruleId}` }],
  },
  { path: '/dashboard/coupons', end: true, crumbs: () => [{ label: 'Coupons' }] },
  { path: '/dashboard/teams/new', end: true, crumbs: () => [{ label: 'Teams', to: '/dashboard/teams' }, { label: 'New team' }] },
  {
    path: '/dashboard/teams/:teamId/members',
    end: true,
    crumbs: (p) => [{ label: 'Teams', to: '/dashboard/teams' }, { label: `Team #${p.teamId} members` }],
  },
  { path: '/dashboard/teams', end: true, crumbs: () => [{ label: 'Teams' }] },
  { path: '/dashboard/collaboration', end: true, crumbs: () => [{ label: 'Collaboration Board' }] },
  { path: '/dashboard/team-files', end: true, crumbs: () => [{ label: 'Team Files' }] },
  { path: '/dashboard/payroll', end: true, crumbs: () => [{ label: 'Payroll' }] },
  {
    path: '/dashboard/class-packages/:packageId',
    end: true,
    crumbs: (p) => [
      { label: 'Group Packages', to: '/dashboard/class-packages' },
      { label: p.packageId ? `#${p.packageId}` : 'Details' },
    ],
  },
  { path: '/dashboard/class-packages', end: true, crumbs: () => [{ label: 'Group Packages' }] },
  {
    path: '/dashboard/automation-tasks/:taskId',
    end: true,
    crumbs: (p) => [
      { label: 'Automation Tasks', to: '/dashboard/automation-tasks' },
      { label: p.taskId ? `Task #${p.taskId}` : 'Detail' },
    ],
  },
  { path: '/dashboard/automation-tasks', end: true, crumbs: () => [{ label: 'Automation Tasks' }] },
  { path: '/dashboard/vendor-bills/new', end: true, crumbs: () => [{ label: 'Vendor Bills', to: '/dashboard/vendor-bills' }, { label: 'New bill' }] },
  {
    path: '/dashboard/vendor-bills/:billId/edit',
    end: true,
    crumbs: (p) => [
      { label: 'Vendor Bills', to: '/dashboard/vendor-bills' },
      { label: `Edit bill #${p.billId}` },
    ],
  },
  {
    path: '/dashboard/vendor-bills/:billId/pay',
    end: true,
    crumbs: (p) => [
      { label: 'Vendor Bills', to: '/dashboard/vendor-bills' },
      { label: `Record payment #${p.billId}` },
    ],
  },
  {
    path: '/dashboard/vendor-bills/:billId',
    end: true,
    crumbs: (p) => [
      { label: 'Vendor Bills', to: '/dashboard/vendor-bills' },
      { label: `Bill #${p.billId}` },
    ],
  },
  { path: '/dashboard/vendor-bills', end: true, crumbs: () => [{ label: 'Vendor Bills' }] },
  { path: '/dashboard/journal/entries/new', end: true, crumbs: () => [{ label: 'Journal', to: '/dashboard/journal' }, { label: 'New entry' }] },
  {
    path: '/dashboard/journal/entries/:entryId',
    end: true,
    crumbs: (p) => [{ label: 'Journal', to: '/dashboard/journal' }, { label: `Entry #${p.entryId}` }],
  },
  { path: '/dashboard/journal/accounts/new', end: true, crumbs: () => [{ label: 'Journal', to: '/dashboard/journal' }, { label: 'New account' }] },
  {
    path: '/dashboard/journal/reports/trial-balance',
    end: true,
    crumbs: () => [{ label: 'Journal', to: '/dashboard/journal' }, { label: 'Trial balance' }],
  },
  {
    path: '/dashboard/journal/reports/journal-register',
    end: true,
    crumbs: () => [{ label: 'Journal', to: '/dashboard/journal' }, { label: 'Journal register' }],
  },
  { path: '/dashboard/journal', end: true, crumbs: () => [{ label: 'Journal' }] },
  { path: '/dashboard/bookings/new', end: true, crumbs: () => [{ label: 'Bookings', to: '/dashboard/bookings' }, { label: 'New entry' }] },
  {
    path: '/dashboard/bookings/:bookingId/edit',
    end: true,
    crumbs: (p) => [{ label: 'Bookings', to: '/dashboard/bookings' }, { label: `Edit #${p.bookingId}` }],
  },
  {
    path: '/dashboard/bookings/:bookingId',
    end: true,
    crumbs: (p) => [{ label: 'Bookings', to: '/dashboard/bookings' }, { label: `Booking #${p.bookingId}` }],
  },
  { path: '/dashboard/bookings', end: true, crumbs: () => [{ label: 'Bookings' }] },
  { path: '/dashboard/schedule/new', end: true, crumbs: () => [{ label: 'Schedule', to: '/dashboard/schedule' }, { label: 'New session' }] },
  {
    path: '/dashboard/schedule/:sessionId/edit',
    end: true,
    crumbs: (p) => [{ label: 'Schedule', to: '/dashboard/schedule' }, { label: `Edit session #${p.sessionId}` }],
  },
  {
    path: '/dashboard/schedule/:sessionId',
    end: true,
    crumbs: (p) => [{ label: 'Schedule', to: '/dashboard/schedule' }, { label: `Session #${p.sessionId}` }],
  },
  { path: '/dashboard/schedule', end: true, crumbs: () => [{ label: 'Schedule' }] },
  { path: '/dashboard/events', end: true, crumbs: () => [{ label: 'Events & Tickets' }] },
  { path: '/dashboard/data-management', end: true, crumbs: () => [{ label: 'Data Management' }] },
  { path: '/dashboard/reports/customer-product-reconcile', end: true, crumbs: () => [{ label: 'Reports', to: '/dashboard/reports' }, { label: 'Customer Product Reconcile Report' }] },
  { path: '/dashboard/reports/subscriptions', end: true, crumbs: () => [{ label: 'Reports', to: '/dashboard/reports' }, { label: 'Subscription Report' }] },
  { path: '/dashboard/reports/staff-sessions', end: true, crumbs: () => [{ label: 'Reports', to: '/dashboard/reports' }, { label: 'Staff Session Report' }] },
  { path: '/dashboard/reports/payments', end: true, crumbs: () => [{ label: 'Reports', to: '/dashboard/reports' }, { label: 'Payment Report' }] },
  { path: '/dashboard/reports/orders', end: true, crumbs: () => [{ label: 'Reports', to: '/dashboard/reports' }, { label: 'Order Report' }] },
  { path: '/dashboard/reports/inventory', end: true, crumbs: () => [{ label: 'Reports', to: '/dashboard/reports' }, { label: 'Inventory Report' }] },
  { path: '/dashboard/reports', end: true, crumbs: () => [{ label: 'Reports' }] },
  { path: '/dashboard/profile', end: true, crumbs: () => [{ label: 'Your profile' }] },
  { path: '/dashboard/settings', end: true, crumbs: () => [{ label: 'Settings' }] },
]

const MODULE_HOME: Record<string, string> = {
  shops: 'Shops',
  customers: 'Customers',
  products: 'Products',
  orders: 'Sales Orders',
  quotations: 'Quotations',
  payments: 'Payments',
  staff: 'Staff',
  brands: 'Brands',
  suppliers: 'Suppliers',
  'purchase-orders': 'Purchase Orders',
  subscription: 'Subscription',
  tags: 'Tags',
  website: 'Website',
  coupons: 'Coupons',
  teams: 'Teams',
  collaboration: 'Collaboration Board',
  'team-files': 'Team Files',
  messaging: 'Messaging',
  payroll: 'Payroll',
  'class-packages': 'Group Packages',
  'automation-tasks': 'Automation Tasks',
  journal: 'Journal',
  'vendor-bills': 'Vendor Bills',
  bookings: 'Bookings',
  schedule: 'Schedule',
  events: 'Events & Tickets',
  'data-management': 'Data Management',
  reports: 'Reports',
  profile: 'Your profile',
  settings: 'Settings',
  admin: 'Admin',
}

function fallbackDashboardCrumbs(pathname: string): AppCrumb[] {
  const segs = pathname.split('/').filter(Boolean)
  if (segs[0] !== 'dashboard') return []
  if (segs.length <= 1) return [{ label: 'Dashboard' }]
  const mod = segs[1]
  const modLabel = MODULE_HOME[mod] ?? titleCase(mod.replace(/-/g, ' '))
  const baseTo = `/dashboard/${mod}`
  if (segs.length === 2) {
    return [{ label: 'Dashboard', to: '/dashboard' }, { label: modLabel }]
  }
  const crumbs: AppCrumb[] = [{ label: 'Dashboard', to: '/dashboard' }, { label: modLabel, to: baseTo }]
  for (let i = 2; i < segs.length; i++) {
    const s = segs[i]
    const isNumeric = /^\d+$/.test(s)
    crumbs.push({ label: isNumeric ? `#${s}` : titleCase(s) })
  }
  return crumbs
}

export function getDashboardBreadcrumbs(pathname: string): AppCrumb[] {
  for (const spec of DASHBOARD_SPECS) {
    const m = matchPath({ path: spec.path, end: spec.end ?? true }, pathname)
    if (m) return spec.crumbs(m.params)
  }
  return fallbackDashboardCrumbs(pathname)
}

const CUSTOMER_SPECS: CrumbSpec[] = [
  { path: '/customer', end: true, crumbs: () => [{ label: 'Home' }] },
  { path: '/customer/products', end: true, crumbs: () => [{ label: 'Catalog' }] },
  { path: '/customer/cart', end: true, crumbs: () => [{ label: 'Cart' }] },
  {
    path: '/customer/orders/:orderId/register-payment',
    end: true,
    crumbs: (p) => [
      { label: 'Orders', to: '/customer/orders' },
      { label: p.orderId ? `Order #${p.orderId}` : 'Order', to: p.orderId ? `/customer/orders/${p.orderId}` : undefined },
      { label: 'Register payment' },
    ],
  },
  {
    path: '/customer/orders/:orderId',
    end: true,
    crumbs: (p) => [
      { label: 'Orders', to: '/customer/orders' },
      { label: p.orderId ? `Order #${p.orderId}` : 'Details' },
    ],
  },
  { path: '/customer/orders', end: true, crumbs: () => [{ label: 'Orders' }] },
  { path: '/customer/downloads', end: true, crumbs: () => [{ label: 'Downloads' }] },
  { path: '/customer/tickets', end: true, crumbs: () => [{ label: 'My Tickets' }] },
  { path: '/customer/coupons', end: true, crumbs: () => [{ label: 'Coupons' }] },
  {
    path: '/customer/subscriptions/:subscriptionId',
    end: true,
    crumbs: (p) => [
      { label: 'Subscriptions', to: '/customer/subscriptions' },
      { label: p.subscriptionId ? `#${p.subscriptionId}` : 'Detail' },
    ],
  },
  { path: '/customer/subscriptions', end: true, crumbs: () => [{ label: 'Subscriptions' }] },
  { path: '/customer/schedule', end: true, crumbs: () => [{ label: 'Schedule' }] },
  { path: '/customer/profile', end: true, crumbs: () => [{ label: 'Profile' }] },
]

export function getCustomerBreadcrumbs(pathname: string): AppCrumb[] {
  for (const spec of CUSTOMER_SPECS) {
    const m = matchPath({ path: spec.path, end: spec.end ?? true }, pathname)
    if (m) return spec.crumbs(m.params)
  }
  return [{ label: 'Home', to: '/customer' }]
}

export function getAppBreadcrumbs(pathname: string): AppCrumb[] {
  if (pathname.startsWith('/customer')) return getCustomerBreadcrumbs(pathname)
  if (pathname.startsWith('/dashboard')) return getDashboardBreadcrumbs(pathname)
  return []
}
